import React from 'react';
import PredictionGrid from 'components/molecules/PredictionGrid';
import { useSelectFilter } from 'hooks/planning/useFilter';
import { IData, IRow } from 'components/molecules/PredictionGrid/types';
import {
  postGridChromatographic,
  postExportChromatograficGrid,
} from 'services/prediction/grid-chromatographic';
import { useQuery } from 'react-query';
import qs from 'querystring';
import { openInNewTab } from 'helpers';
import { CHROMATOGRAPHIC_GRID_VALIDATION_RULES } from 'constants/grid/validationRules';
import { columns } from './data';

const TAKE = 10;
const MAX_ROWS = 100000;

const mapRows = result => {
  return result.map(row => {
    return {
      id: row.id,
      collectDate: row.collectDate,
      serieNumber: row.serieNumber,
      tagCode: row.tagCode,
      h2: row.h2,
      o2: row.o2,
      n2: row.n2,
      co2: row.co2,
      c2h4: row.c2h4,
      c2h6: row.c2h6,
      c2h2: row.c2h2,
      co: row.co,
      ch4: row.ch4,
      tgc: row.tgc,
      iecC57: row.iecC57,
      rateValue: row.rateValue,
      classification: row.classification,
      omegaClassification: row.omegaClassification,
      recommendation: row.recommendation,
      omegaRecommendation: row.omegaRecommendation,
    };
  });
};

const PredictionGridChromatographicContainer = () => {
  // INÍCIO ESTADO DOS FILTROS
  const [gridRows, setGridRows] = React.useState<IRow[]>([]);
  const [exportingGridData, setExportingGridData] =
    React.useState<boolean>(false);
  const [
    installationFilterOptionsContext,
    serialNumberFilterOptionsContext,
    selectedInitialDateOptionContext,
    selectedFinalDateOptionContext,
  ] = [
    useSelectFilter('installation'),
    useSelectFilter('serial-number'),
    useSelectFilter('initialDate'),
    useSelectFilter('finalDate'),
  ];

  const allFilterContexts = React.useMemo(
    () => [
      serialNumberFilterOptionsContext,
      installationFilterOptionsContext,
      selectedInitialDateOptionContext,
      selectedFinalDateOptionContext,
    ],
    [
      serialNumberFilterOptionsContext,
      installationFilterOptionsContext,
      selectedInitialDateOptionContext,
      selectedFinalDateOptionContext,
    ],
  );

  // INÍCIO ESTADO DAS CONFIGURAÇÕES DAS GRID

  const [dataGrid, setDataGrid] = React.useState(() => columns);

  // FIM ESTADO DAS CONFIGURAÇÕES DAS GRID

  // INÍCIO ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID
  const [totalRows, setTotalRows] = React.useState(MAX_ROWS);
  const [currentPage, setCurrentPage] = React.useState<number>(0);
  const [pageSize, setPageSize] = React.useState<number>(TAKE);
  const pageSizes = React.useMemo(() => [10, 20, 30, 40, 50, 100], []);
  // FIM ESTADO DOS ARGUMENTOS DE PAGINAÇÃO DO GRID

  const installationIds = installationFilterOptionsContext.map(item =>
    String(item.value),
  );

  const serieNumber = serialNumberFilterOptionsContext.map(item =>
    String(item.value),
  );

  const startDateMap = selectedInitialDateOptionContext.map(item =>
    String(item.value),
  );

  const start = React.useMemo(() => startDateMap[0], [startDateMap]);

  const endDateMap = selectedFinalDateOptionContext.map(item =>
    String(item.value),
  );

  const end = React.useMemo(() => endDateMap[0], [endDateMap]);
  const gridOrder = React.useMemo(
    () =>
      dataGrid
        .filter(column => column.direction)
        .map(column => ({
          columnName: column.name,
          order: column?.direction?.toLocaleUpperCase(),
        })),
    [dataGrid],
  );

  // AO ALTERAR OS FILTROS, DEVERÁ RETORNAR PARA PÁGINA INICIAL
  React.useEffect(() => {
    setCurrentPage(0);
  }, [allFilterContexts, pageSize]);

  const queryPredictionChromatographicGrid = useQuery({
    queryKey: [
      'PredictionChromatographicGridDataContext',
      [pageSize, currentPage, gridOrder, ...allFilterContexts],
    ],
    queryFn: async () => {
      const qsparams = qs.stringify({
        page: currentPage + 1,
        take: pageSize,
      });
      const params = {
        order: gridOrder,
        installationIds,
        serieNumber,
        start,
        end,
      };

      const { data } = await postGridChromatographic(qsparams, params);
      return data;
    },
    select: data => {
      const rows: IRow[] = mapRows(data.result);
      return {
        count: data.count,
        rows,
      };
    },
    onSuccess: ({ count, rows }: { rows: IRow[]; count: number }) => {
      setGridRows(rows);
      setTotalRows(count);
    },
    enabled: true,
    cacheTime: 0,
  });

  const getRowId = React.useCallback((row: IRow) => String(row.id), []);

  const onSortingChange = React.useCallback(sortedList => {
    setDataGrid(oldDataGrid =>
      oldDataGrid.map(item => {
        const foundItem = sortedList.find(
          sortItem => sortItem.columnName === item.name,
        );
        if (foundItem) {
          return {
            ...item,
            direction: foundItem.direction,
          };
        }
        const newItem = { ...item };
        delete newItem.direction;
        return newItem;
      }),
    );
  }, []);

  const getRowById = React.useCallback(
    id => {
      const strId = `${id}`;
      return gridRows.find(row => `${row?.id}` === strId);
    },
    [gridRows],
  );

  const refetch = React.useCallback(() => {
    queryPredictionChromatographicGrid.refetch();
  }, [queryPredictionChromatographicGrid]);

  const exportGridData = React.useCallback(() => {
    setExportingGridData(true);

    const params = {
      order: gridOrder,
      installationIds,
      serieNumber,
      start,
      end,
    };

    postExportChromatograficGrid(params)
      .then(({ url }) => {
        openInNewTab(url);
      })
      .finally(() => setExportingGridData(false));
  }, [serieNumber, start, end, gridOrder, installationIds]);

  return (
    <PredictionGrid
      columns={dataGrid as IData[]}
      rows={gridRows}
      loading={
        queryPredictionChromatographicGrid?.isLoading || exportingGridData
      }
      dayMonthYearCells={['collectDate']}
      tooltipCells={[
        'h2',
        'co2',
        'n2',
        'o2',
        'c2h4',
        'c2h6',
        'ch4',
        'co',
        'tgc',
        'c2h2',
        'rateValue',
      ]}
      tooltipRecommendationCells={['recommendation']}
      validationRules={CHROMATOGRAPHIC_GRID_VALIDATION_RULES}
      tooltipMaxStrLength={5}
      onSortingChange={onSortingChange}
      getRowId={getRowId}
      getRowById={getRowById}
      currentPage={currentPage}
      pageSize={pageSize}
      pageSizes={pageSizes}
      totalCount={totalRows}
      refetch={refetch}
      onCurrentPageChange={setCurrentPage}
      onPageSizeChange={setPageSize}
      onExport={exportGridData}
      serieNumber={serieNumber}
      start={start}
      end={end}
    />
  );
};

export default PredictionGridChromatographicContainer;
